// ./src/pages/private/HomePage/homeWidgets.tsx
//
// The Home page's cards (ROADMAP A2b #50). Presentational only — every rule they render comes from
// `@/utils/homeOverview`, which `npm run check-home` runs. Nothing here decides anything.
//
// ⚠ The widget shell is the SHARED `DashboardWidget`, not a copy: Home and the event dashboard are
// two dashboards in one product and they have to keep looking like it.

import React from 'react';
import { Badge, Box, Button, Card, Divider, Grid, Group, Skeleton, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { AppIcon, DashboardWidget, EmptyState } from '@/components/generic';
import { EventCard } from '@/components/features';
import { WeatherMultiDayStrip, WeatherPanel } from '@/components/features/Weather';
import { ONBOARDING_STEP, onboardingStepPath } from '@/components/features/Onboarding/onboardingSteps';
import { eventStatusMeta } from '@/components/features/Events/eventListHelpers';
import { useOptionalEventSetupCatalogContext } from '@/contexts/EventSetupCatalogContext';
import { formatDateTime } from '@/utils';
import { Event, EventGuestStats, HomeWeather, Task } from '@/utils/api';
import {
  AttentionItem,
  AttentionSummary,
  ChecklistStep,
  Countdown,
  EVENT_STATUS_PUBLISHED,
  SpotlightKind,
  attentionPath,
  eventCountdown,
  eventPath,
} from '@/utils/homeOverview';

// Full width on phones, half on desktop — the same span the event dashboard uses.
export const HALF = { base: 12, md: 6 } as const;

// ── Spotlight ────────────────────────────────────────────────────────────────

const CountdownBadge: React.FC<{ countdown: Countdown }> = ({ countdown }) => {
  const { t } = useTranslation(['home']);

  if (countdown.kind === 'unscheduled') {
    return (
      <Badge size="md" variant="light" color="gray">
        {t('home:countdown.unscheduled', { defaultValue: 'No date yet' })}
      </Badge>
    );
  }

  const label =
    countdown.kind === 'now'
      ? t('home:countdown.now', { defaultValue: 'Happening now' })
      : countdown.kind === 'today'
        ? t('home:countdown.today', { defaultValue: 'Today' })
        : countdown.kind === 'tomorrow'
          ? t('home:countdown.tomorrow', { defaultValue: 'Tomorrow' })
          : t('home:countdown.days', { defaultValue: 'In {{count}} days', count: countdown.days });

  const color = countdown.kind === 'now' ? 'emerald' : countdown.kind === 'today' ? 'orange' : 'brand';

  return (
    <Badge size="md" variant={countdown.kind === 'days' ? 'light' : 'filled'} color={color}>
      {label}
    </Badge>
  );
};

export interface HomeSpotlightProps {
  event: Event;
  kind: SpotlightKind;
  stats: EventGuestStats | null;
  statsLoading: boolean;
  onGo: (path: string) => void;
}

export const HomeSpotlight: React.FC<HomeSpotlightProps> = ({ event, kind, stats, statsLoading, onGo }) => {
  const { t } = useTranslation(['home', 'events']);
  // #62 — the type (and its emoji) from the one event-type catalog; null while it loads.
  const type = useOptionalEventSetupCatalogContext()?.findType(event.eventCategoryId) ?? null;
  const status = eventStatusMeta(event.eventStatus);
  const countdown = eventCountdown(event);
  const eventId = event.id ?? '';

  // ⚠ In the EVENT's time zone (2026-10-04) — the one its dates are entered in; never this device's.
  const eventTz = event.timezone || undefined;
  const whenText = event.nextDateStartsAt
    ? t('events:list.nextDate', {
        defaultValue: 'Next: {{when}}',
        when: formatDateTime(event.nextDateStartsAt, event.isAllDay ? 'long' : 'datetime', eventTz),
      })
    : event.startsAt
      ? formatDateTime(event.startsAt, event.isAllDay ? 'long' : 'datetime', eventTz)
      : t('events:list.dateTbd', { defaultValue: 'Date TBD' });

  // "Check in" only matters while it is on — a check-in button three weeks early is a dead control.
  const showCheckin = countdown.kind === 'now' || countdown.kind === 'today';
  const showWebsite = event.eventStatus === EVENT_STATUS_PUBLISHED;

  const heading =
    kind === 'now'
      ? t('home:spotlight.now', { defaultValue: 'Happening now' })
      : kind === 'planning'
        ? t('home:spotlight.planning', { defaultValue: 'In planning' })
        : t('home:spotlight.next', { defaultValue: 'Your next event' });

  return (
    <Card withBorder radius="md" padding="lg">
      <Text size="md" c="dimmed" fw={600} mb="xs">
        {heading}
      </Text>

      <Group justify="space-between" wrap="wrap" gap="md" align="center">
        <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
          <ThemeIcon variant="light" color="brand" size={54} radius="md" style={{ flexShrink: 0, fontSize: 28 }}>
            <span aria-hidden>{type?.emoji ?? '📅'}</span>
          </ThemeIcon>
          <Stack gap={4} style={{ minWidth: 0 }}>
            <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
              <Title order={3} style={{ wordBreak: 'break-word' }}>
                {event.title || t('events:list.untitled', { defaultValue: 'Untitled event' })}
              </Title>
              <Badge size="md" variant="light" color={status.color} style={{ flexShrink: 0 }}>
                {status.label}
              </Badge>
            </Group>
            <Group gap="xs" wrap="wrap">
              <CountdownBadge countdown={countdown} />
              <Text size="md" c="dimmed">
                {whenText}
              </Text>
            </Group>
          </Stack>
        </Group>
      </Group>

      <Divider my="md" />

      {/* ⚠ THREE NUMBERS, NOT THE DASHBOARD. The event dashboard is one click away and owns the
          detail; repeating it here would make Home a worse copy of a page that already exists. */}
      {statsLoading && !stats ? (
        <Skeleton height={20} width={260} mb="md" />
      ) : stats ? (
        <Group gap="lg" wrap="wrap" mb="md">
          <Text size="md">
            <Text span fw={700}>
              {stats.totalGuests ?? 0}
            </Text>{' '}
            {t('home:spotlight.onTheList', { defaultValue: 'on the guest list' })}
          </Text>
          {stats.capacity != null && (
            <Text size="md">
              <Text span fw={700}>
                {stats.placesLeft ?? 0}
              </Text>{' '}
              {t('home:spotlight.placesLeft', { defaultValue: 'places left' })}
            </Text>
          )}
        </Group>
      ) : null}

      <Group gap="xs" wrap="wrap">
        <Button onClick={() => onGo(eventPath(eventId))} leftSection={<AppIcon iconName="LayoutDashboard" size={16} />}>
          {t('home:spotlight.open', { defaultValue: 'Open event' })}
        </Button>
        <Button variant="default" onClick={() => onGo(eventPath(eventId, 'guests'))} leftSection={<AppIcon iconName="Users" size={16} />}>
          {t('home:spotlight.guests', { defaultValue: 'Guest list' })}
        </Button>
        {showCheckin && (
          <Button variant="default" onClick={() => onGo(eventPath(eventId, 'checkin'))} leftSection={<AppIcon iconName="Qrcode" size={16} />}>
            {t('home:spotlight.checkin', { defaultValue: 'Check-in' })}
          </Button>
        )}
        {showWebsite && (
          <Button variant="default" onClick={() => onGo(eventPath(eventId, 'website'))} leftSection={<AppIcon iconName="World" size={16} />}>
            {t('home:spotlight.website', { defaultValue: 'Website' })}
          </Button>
        )}
      </Group>
    </Card>
  );
};

// ── Weather ──────────────────────────────────────────────────────────────────

export interface HomeWeatherWidgetProps {
  weather: HomeWeather;
}

/**
 * The forecast for the next event's venue — the rest of today by the hour, then the following days.
 *
 * ⚠ NOT "AROUND THE EVENT", which is what the event dashboard shows. On Home the question is "what is
 * the weather doing", so the window starts at NOW: a host opening this on Tuesday for a Saturday event
 * wants today's rain on the way to the venue as much as Saturday's.
 * ⚠ The forecast is handed over by `myHomeAttention` — it was already fetched there for the risk
 * check, and it is cached server-side, so rendering this costs no extra provider call.
 *
 * ⚠ REUSES the ONE weather renderer (`components/features/Weather`), which was extracted out of the
 * event dashboard for exactly this. A second condition-code mapping would drift within a month.
 */
export const HomeWeatherWidget: React.FC<HomeWeatherWidgetProps> = ({ weather }) => {
  const { t } = useTranslation(['home']);
  const now = new Date();
  const forecast = weather.forecast ?? null;

  // ⚠ `days[0]` is TODAY, which the hourly strip above already covers — the following days start at 1.
  const following = (forecast?.days ?? []).slice(1, 4);

  /**
   * ⚠⚠ ONE CARD, NOT TWO. This first shipped as the hourly panel and the day strip stacked — two
   * bordered cards, both headed "Weather", about the same place. Two cards read as two subjects; the
   * days belong INSIDE the forecast, under a divider, which is why the strip draws no card of its own
   * and is handed over as the panel's footer.
   */
  return (
    <WeatherPanel
      startsAt={now.toISOString()}
      endsAt={new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString()}
      timezone={weather.timezone ?? forecast?.timezone ?? null}
      forecast={forecast}
      loading={false}
      locationLabel={weather.locationLabel ?? weather.eventTitle ?? null}
      footer={
        following.length > 0 ? (
          <WeatherMultiDayStrip
            days={following}
            caption={t('home:weather.following', { defaultValue: 'The next few days' })}
          />
        ) : null
      }
    />
  );
};

// ── Needs attention ──────────────────────────────────────────────────────────

/**
 * ⚠ EVERY KIND NEEDS AN ICON THE REGISTRY ACTUALLY HAS. An `iconName` the generated registry does not
 * know renders a silent QUESTION MARK — no error, no warning, just a wrong glyph on the first screen
 * of the product. `npm run check-home` asserts each one against the registry.
 */
const ATTENTION_ICON: Record<string, string> = {
  guest_requests: 'UserCheck',
  sponsor_applications: 'Building',
  volunteer_shifts: 'HeartHandshake',
  weather_risk: 'CloudStorm',
  budget_due: 'Coin',
  travel_soon: 'Plane',
  rental_soon: 'Car',
  stay_soon: 'Bed',
  inventory_expiring: 'Clock',
  inventory_low: 'Package',
  artist_pending: 'Microphone',
  vendors_overdue: 'Clock',
  vendors_unreachable: 'Mail',
  vendors_email_undelivered: 'Mail',
  artist_hold_expiring: 'Hourglass',
  registration_at_risk: 'QuestionMark',
  event_underfunded: 'ReportMoney',
  card_payments_off: 'CreditCard',
};

const SEVERITY_COLOR: Record<string, string> = {
  urgent: 'red',
  warn: 'orange',
  info: 'blue',
};

export interface AttentionWidgetProps {
  summary: AttentionSummary;
  /** A re-read is in flight while the previous counts stay on screen. */
  refreshing?: boolean;
  onGo: (path: string) => void;
  onRetry: () => void;
}

export const AttentionWidget: React.FC<AttentionWidgetProps> = ({ summary, refreshing, onGo, onRetry }) => {
  const { t } = useTranslation(['home']);

  /**
   * The sentence for one row.
   *
   * ⚠ COMPOSED HERE, FROM CODES AND NUMBERS. The server deliberately sends neither prose nor a
   * pre-formatted count — this product ships four languages, and an English sentence built on the
   * server cannot be translated by any of them.
   */
  const labelFor = (item: AttentionItem): string => {
    const count = item.count;

    switch (item.kind) {
      case 'guest_requests':
        return t('home:attention.guestRequests', { defaultValue: '{{count}} guest requests to review', count });
      case 'sponsor_applications':
        return t('home:attention.sponsorApplications', { defaultValue: '{{count}} sponsor applications waiting', count });
      case 'volunteer_shifts':
        // ⚠ APPLICATIONS, not shifts — the server counts people, because one volunteer asking for
        // three shifts is ONE decision and "3 to confirm" about one person overstates the queue.
        return t('home:attention.volunteerShifts', { defaultValue: '{{count}} volunteers to confirm', count });
      case 'weather_risk':
        return t('home:attention.weatherRisk', { defaultValue: 'The forecast is against this event' });
      case 'budget_due':
        return t('home:attention.budgetDue', { defaultValue: '{{count}} payments due soon', count });
      case 'travel_soon':
        return t('home:attention.travelSoon', { defaultValue: '{{count}} journeys today or tomorrow', count });
      case 'rental_soon':
        return t('home:attention.rentalSoon', { defaultValue: '{{count}} rentals to collect or return', count });
      case 'stay_soon':
        return t('home:attention.staySoon', { defaultValue: '{{count}} guests checking in', count });
      case 'inventory_expiring':
        return t('home:attention.inventoryExpiring', { defaultValue: '{{count}} stock lots expiring', count });
      case 'inventory_low':
        return t('home:attention.inventoryLow', { defaultValue: '{{count}} items low on stock', count });
      case 'vendors_overdue':
        return t('home:attention.vendorsOverdue', { defaultValue: '{{count}} vendors with overdue documents', count });
      case 'vendors_email_undelivered':
        return t('home:attention.vendorsEmailUndelivered', { defaultValue: '{{count}} vendors whose email did not arrive', count });
      case 'vendors_unreachable':
        return t('home:attention.vendorsUnreachable', { defaultValue: '{{count}} vendors we cannot remind (nobody gets our emails)', count });
      case 'artist_pending':
        return t('home:attention.artistPending', { defaultValue: '{{count}} bookings waiting on a reply', count });
      case 'artist_hold_expiring':
        return t('home:attention.artistHoldExpiring', { defaultValue: '{{count}} holds about to lapse', count });
      case 'registration_at_risk':
        return t('home:attention.registrationAtRisk', { defaultValue: 'Registration closes soon and almost nobody has signed up' });
      // ⚠ Deliberately says MONEY, not "at risk". This event may be selling perfectly well — the
      // problem is that what is coming in is not on course to meet what it costs, and the fix is a
      // different one from "invite more people". A shared, vaguer sentence for both warnings would
      // send hosts to the wrong screen.
      case 'event_underfunded':
        return t('home:attention.eventUnderfunded', { defaultValue: 'Ticket sales are not on course to cover this event' });
      // ROADMAP #161 b — what nobody can pay for is in the detail line (`reasons`); the fix is the same for all of it.
      case 'card_payments_off':
        return t('home:attention.cardPaymentsOff', { defaultValue: 'Buyers cannot pay — connect Stripe' });
      default:
        return item.kind;
    }
  };

  /** The second line: what the host needs to know without opening it. */
  const detailFor = (item: AttentionItem): string | null => {
    const parts: string[] = [];

    if (item.kind === 'weather_risk' && item.reasons?.length) {
      parts.push(
        item.reasons
          .map((reason) =>
            reason === 'rain'
              ? t('home:weather.rain', { defaultValue: 'rain' })
              : reason === 'wind'
                ? t('home:weather.wind', { defaultValue: 'high wind' })
                : reason === 'heat'
                  ? t('home:weather.heat', { defaultValue: 'extreme heat' })
                  : t('home:weather.cold', { defaultValue: 'freezing' }),
          )
          .join(' · '),
      );
    }

    // #161 b — WHAT is sold that nobody can pay for. A code this build does not know is left out, never printed.
    if (item.kind === 'card_payments_off' && item.reasons?.length) {
      const sold = item.reasons
        .map((reason) =>
          reason === 'tickets'
            ? t('home:attention.sells.tickets', { defaultValue: 'tickets' })
            : reason === 'signup_items'
              ? t('home:attention.sells.signupItems', { defaultValue: 'sign-up items' })
              : reason === 'sponsorships'
                ? t('home:attention.sells.sponsorships', { defaultValue: 'sponsorships' })
                : '',
        )
        .filter(Boolean);

      if (sold.length > 0) {
        parts.push(sold.join(', '));
      }
    }

    // ⚠ Named only when the server said exactly ONE person is responsible — it sends null otherwise,
    // because "Due: Alex Chen" over three people's invoices is a specific, confident lie.
    if (item.who) {
      parts.push(item.who);
    }

    if (item.kind === 'registration_at_risk' && item.capacity) {
      parts.push(t('home:attention.ofCapacity', { defaultValue: '{{count}} of {{capacity}}', count: item.count, capacity: item.capacity }));
    }

    // ⚠ SHOWS THE NUMBER IT WAS JUDGED BY, not just the verdict. A host who cannot see which
    // threshold tripped cannot tell a real problem from a threshold that wants tuning — and the
    // threshold is one they (or their account default) chose, so it is theirs to recognise.
    if (item.kind === 'event_underfunded' && item.coverage != null) {
      parts.push(
        t('home:attention.costsCovered', {
          defaultValue: '{{covered}}% of costs covered, target {{target}}%',
          covered: Math.round(item.coverage * 100),
          target: Math.round((item.threshold ?? 1) * 100),
        }),
      );
    }

    if (item.at) {
      // #137 — on the two "registration closes soon" kinds `at` is the DEADLINE, a moment (a date and a time): show the
      // time too, in this viewer's clock (the item carries no zone, and a true moment in their clock is never wrong).
      const atIsDeadline = item.kind === 'registration_at_risk' || item.kind === 'event_underfunded';
      parts.push(formatDateTime(item.at, atIsDeadline ? 'datetime' : 'date'));
    }

    if (item.eventTitle) {
      parts.push(item.eventTitle);
    }

    return parts.length > 0 ? parts.join(' · ') : null;
  };

  return (
    <DashboardWidget
      title={t('home:attention.title', { defaultValue: 'Needs attention' })}
      iconName="AlertCircle"
      iconColor="orange"
      loading={summary.loading || !!refreshing}
    >
      <Stack gap="xs">
        {summary.items.map((item) => {
          const target = attentionPath(item.kind, item.eventId);
          const color = SEVERITY_COLOR[item.severity] ?? 'orange';
          const detail = detailFor(item);

          return (
            <Card
              key={`${item.kind}:${item.eventId ?? 'account'}`}
              withBorder
              radius="sm"
              padding="sm"
              role="button"
              tabIndex={0}
              style={{ cursor: 'pointer' }}
              onClick={() => onGo(target)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onGo(target);
                }
              }}
            >
              <Group gap="sm" wrap="nowrap">
                <ThemeIcon variant="light" color={color} size={28} radius="md" style={{ flexShrink: 0 }}>
                  <AppIcon iconName={ATTENTION_ICON[item.kind] ?? 'AlertCircle'} size={15} />
                </ThemeIcon>
                <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                  <Text size="md" truncate>
                    {labelFor(item)}
                  </Text>
                  {detail && (
                    <Text size="sm" c="dimmed" truncate>
                      {detail}
                    </Text>
                  )}
                </Stack>
                <AppIcon iconName="ChevronRight" size={16} />
              </Group>
            </Card>
          );
        })}

        {summary.moreCount > 0 && (
          <Text size="sm" c="dimmed">
            {t('home:attention.more', { defaultValue: 'and {{count}} more', count: summary.moreCount })}
          </Text>
        )}

        {/* ⚠⚠ "ALL CAUGHT UP" ONLY WHEN EVERY SOURCE ANSWERED — both the client reads AND the server
            query, including its own failedKinds. A failed read rendering as zero puts a green tick
            over an unworked queue and gives the host a reason not to look. */}
        {summary.allClear && (
          <Group gap="xs" wrap="nowrap">
            <ThemeIcon variant="light" color="emerald" size={28} radius="md">
              <AppIcon iconName="Check" size={15} />
            </ThemeIcon>
            <Text size="md" c="dimmed">
              {t('home:attention.allClear', { defaultValue: "You're all caught up." })}
            </Text>
          </Group>
        )}

        {summary.incomplete && (
          <Group gap="xs" wrap="wrap" justify="space-between">
            <Text size="md" c="dimmed">
              {t('home:attention.incomplete', { defaultValue: "Some checks couldn't be loaded, so this list may be incomplete." })}
            </Text>
            <Button size="compact-sm" variant="subtle" onClick={onRetry}>
              {t('home:attention.retry', { defaultValue: 'Try again' })}
            </Button>
          </Group>
        )}

        {!summary.allClear && !summary.incomplete && summary.items.length === 0 && summary.loading && (
          <>
            <Skeleton height={44} radius="sm" />
            <Skeleton height={44} radius="sm" />
          </>
        )}
      </Stack>
    </DashboardWidget>
  );
};

// ── Tasks ────────────────────────────────────────────────────────────────────

export interface HomeTasksWidgetProps {
  tasks: Task[];
  loading: boolean;
  /**
   * ⚠ REQUIRED — whether the FIRST read has come back. A skeleton belongs to that read only; with
   * `loading && items.length === 0` the widget instead said "Nothing scheduled" on the very first
   * render, before the request had started, and again on every refresh that came back empty.
   */
  isLoaded: boolean;

  /** eventId → title, so a task says which event it belongs to without another query. */
  eventTitles: Record<string, string>;
  onGo: (path: string) => void;
}

export const HomeTasksWidget: React.FC<HomeTasksWidgetProps> = ({ tasks, loading, isLoaded, eventTitles, onGo }) => {
  const { t } = useTranslation(['home']);
  // ⚠ Only SCHEDULED tasks. An undated task under a heading that says "next" is not next — it is
  // simply a task, and it would push a genuinely overdue one off a five-row card.
  const scheduled = tasks.filter((task) => !!task.scheduledAt);
  const now = Date.now();

  return (
    <DashboardWidget title={t('home:tasks.title', { defaultValue: 'Your next tasks' })} iconName="ListCheck" loading={loading}>
      <Stack gap="xs">
        {!isLoaded && (
          <>
            <Skeleton height={34} radius="sm" />
            <Skeleton height={34} radius="sm" />
          </>
        )}

        {isLoaded && scheduled.length === 0 && (
          <Text size="md" c="dimmed">
            {t('home:tasks.none', { defaultValue: 'Nothing scheduled.' })}
          </Text>
        )}

        {scheduled.map((task) => {
          const overdue = !!task.scheduledAt && Date.parse(task.scheduledAt) < now;
          const eventTitle = task.eventId ? eventTitles[task.eventId] : undefined;

          return (
            <Group
              key={task.id}
              gap="sm"
              wrap="nowrap"
              role="button"
              tabIndex={0}
              style={{ cursor: 'pointer' }}
              onClick={() => task.eventId && onGo(eventPath(task.eventId, 'tasks'))}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && task.eventId) {
                  e.preventDefault();
                  onGo(eventPath(task.eventId, 'tasks'));
                }
              }}
            >
              <Box mt={2} style={{ flexShrink: 0 }} c={overdue ? 'red' : 'dimmed'}>
                <AppIcon iconName={overdue ? 'AlertTriangle' : 'Clock'} size={16} />
              </Box>
              <Stack gap={0} style={{ minWidth: 0, flex: 1 }}>
                <Text size="md" truncate>
                  {task.title || t('home:tasks.untitled', { defaultValue: 'Untitled task' })}
                </Text>
                {/* ⚠ The secondary line stays DIMMED even when the task is late — "overdue" is said by
                    the badge and the red icon, which survive a colour-blind reader and the
                    no-small-text rule's one exemption (a dimmed secondary line) at the same time. */}
                <Group gap="xs" wrap="nowrap">
                  <Text size="sm" c="dimmed" truncate>
                    {formatDateTime(task.scheduledAt, 'datetime')}
                    {eventTitle ? ` · ${eventTitle}` : ''}
                  </Text>
                  {overdue && (
                    <Badge size="sm" variant="light" color="red" style={{ flexShrink: 0 }}>
                      {t('home:tasks.overdue', { defaultValue: 'Overdue' })}
                    </Badge>
                  )}
                </Group>
              </Stack>
            </Group>
          );
        })}
      </Stack>
    </DashboardWidget>
  );
};

// ── Upcoming ─────────────────────────────────────────────────────────────────

export interface UpcomingWidgetProps {
  events: Event[];
  loading: boolean;
  /**
   * ⚠ REQUIRED — whether the FIRST read has come back. A skeleton belongs to that read only; with
   * `loading && items.length === 0` the widget instead said "Nothing scheduled" on the very first
   * render, before the request had started, and again on every refresh that came back empty.
   */
  isLoaded: boolean;

  onOpen: (event: Event) => void;
  onViewAll: () => void;
}

export const UpcomingWidget: React.FC<UpcomingWidgetProps> = ({ events, loading, isLoaded, onOpen, onViewAll }) => {
  const { t } = useTranslation(['home']);

  return (
    <DashboardWidget
      title={t('home:upcoming.title', { defaultValue: 'Coming up' })}
      iconName="CalendarEvent"
      loading={loading}
      action={
        <Button size="compact-sm" variant="subtle" onClick={onViewAll}>
          {t('home:upcoming.viewAll', { defaultValue: 'View all' })}
        </Button>
      }
    >
      <Stack gap="xs">
        {!isLoaded && (
          <>
            <Skeleton height={70} radius="md" />
            <Skeleton height={70} radius="md" />
          </>
        )}

        {isLoaded && events.length === 0 && (
          <Text size="md" c="dimmed">
            {t('home:upcoming.none', { defaultValue: 'Nothing else on the calendar.' })}
          </Text>
        )}

        {events.map((event) => (
          <EventCard key={event.id} event={event} onOpen={onOpen} />
        ))}
      </Stack>
    </DashboardWidget>
  );
};

// ── Getting started ──────────────────────────────────────────────────────────

export interface ChecklistWidgetProps {
  steps: ChecklistStep[];
  /** The event "publish it" should open. Null when there is none yet. */
  publishEventId: string | null;
  onGo: (path: string) => void;
  onDismiss: () => void;
}

const CHECKLIST_PATH: Record<ChecklistStep['key'], string> = {
  // #161 — the account onboarding's email step (a code to type, then it is done).
  confirmEmail: onboardingStepPath(ONBOARDING_STEP.EMAIL),
  createEvent: '/events/create',
  publishEvent: '/events/all',
  getPaid: '/money/stripe-connect',
  saveVenue: '/settings/preferences',
  // #198 b + c — the onboarding's organizer-page step (create it, or add its logo).
  organizerPage: onboardingStepPath(ONBOARDING_STEP.ORGANIZER),
};

export const ChecklistWidget: React.FC<ChecklistWidgetProps> = ({ steps, publishEventId, onGo, onDismiss }) => {
  const { t } = useTranslation(['home']);

  // ⚠ "Publish it" is an instruction about ONE event, and publishing happens on that event's own
  // page. Sending the host to the event list makes them do the lookup the step already knows the
  // answer to; the list is only the fallback when there is no event to point at yet.
  const pathFor = (key: ChecklistStep['key']): string =>
    key === 'publishEvent' && publishEventId ? eventPath(publishEventId) : CHECKLIST_PATH[key];

  const labelFor = (key: ChecklistStep['key']): string => {
    if (key === 'confirmEmail') {
      return t('home:checklist.confirmEmail', { defaultValue: 'Confirm your email address' });
    }

    if (key === 'createEvent') {
      return t('home:checklist.createEvent', { defaultValue: 'Create your first event' });
    }

    if (key === 'publishEvent') {
      return t('home:checklist.publishEvent', { defaultValue: 'Publish it so guests can find it' });
    }

    if (key === 'saveVenue') {
      return t('home:checklist.saveVenue', { defaultValue: 'Save your venue, so new events start there' });
    }

    if (key === 'organizerPage') {
      return t('home:checklist.organizerPage', { defaultValue: 'Create your organizer page, with your logo' });
    }

    // #161 b — shown only to a host who sells something paid, so it says what it is for.
    return t('home:checklist.getPaid', { defaultValue: 'Connect Stripe so buyers can pay' });
  };

  return (
    <DashboardWidget
      title={t('home:checklist.title', { defaultValue: 'Getting started' })}
      iconName="Rocket"
      iconColor="grape"
      action={
        <Button size="compact-sm" variant="subtle" color="gray" onClick={onDismiss}>
          {t('home:checklist.dismiss', { defaultValue: 'Dismiss' })}
        </Button>
      }
    >
      <Stack gap="xs">
        {steps.map((step) => (
          <Group
            key={step.key}
            gap="sm"
            wrap="nowrap"
            role={step.done ? undefined : 'button'}
            tabIndex={step.done ? undefined : 0}
            style={{ cursor: step.done ? 'default' : 'pointer' }}
            onClick={() => !step.done && onGo(pathFor(step.key))}
            onKeyDown={(e) => {
              if (!step.done && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                onGo(pathFor(step.key));
              }
            }}
          >
            <ThemeIcon variant="light" color={step.done ? 'emerald' : 'gray'} size={26} radius="xl" style={{ flexShrink: 0 }}>
              <AppIcon iconName={step.done ? 'Check' : 'Circle'} size={14} />
            </ThemeIcon>
            <Text size="md" c={step.done ? 'dimmed' : undefined} td={step.done ? 'line-through' : undefined}>
              {labelFor(step.key)}
            </Text>
          </Group>
        ))}
      </Stack>
    </DashboardWidget>
  );
};

// ── First run + crew ─────────────────────────────────────────────────────────

export const HomeFirstRun: React.FC<{ onCreate: () => void }> = ({ onCreate }) => {
  const { t } = useTranslation(['home']);

  return (
    <EmptyState
      iconName="CalendarPlus"
      size="lg"
      title={t('home:firstRun.title', { defaultValue: 'Let’s get your first event up' })}
      description={t('home:firstRun.description', {
        defaultValue:
          'Everything else — guests, tickets, the event website — starts from an event. This takes a couple of minutes.',
      })}
      actionLabel={t('home:firstRun.action', { defaultValue: 'Create your first event' })}
      actionIcon="CalendarPlus"
      onAction={onCreate}
    />
  );
};

/**
 * ⚠⚠ A CREW SESSION HAS NO ACCOUNT, so every account-wide read on this page comes back empty and
 * would render as a cheerful "you're all caught up" over an account they cannot see. Crew get their
 * own answer instead, and none of those reads run.
 */
export const CrewHome: React.FC<{ eventId: string | null; onGo: (path: string) => void }> = ({ eventId, onGo }) => {
  const { t } = useTranslation(['home']);

  // ⚠ A password-crew session belongs to exactly ONE event, and `/crew` is an account-user page they
  // cannot reach. Send them to their own event; `/crew` is only the fallback for a session whose
  // stored event id has been lost.
  const target = eventId ? eventPath(eventId) : '/crew';

  return (
    <Card withBorder radius="md" padding="lg">
      <Group gap="sm" wrap="nowrap" mb="sm">
        <ThemeIcon variant="light" color="brand" size={40} radius="md">
          <AppIcon iconName="Users" size={20} />
        </ThemeIcon>
        <Stack gap={0}>
          <Text fw={600} size="md">
            {t('home:crew.title', { defaultValue: "You're signed in as crew" })}
          </Text>
          <Text size="md" c="dimmed">
            {t('home:crew.description', { defaultValue: 'Your events are the ones you have been given access to.' })}
          </Text>
        </Stack>
      </Group>
      <Button onClick={() => onGo(target)} leftSection={<AppIcon iconName="CalendarEvent" size={16} />}>
        {t('home:crew.action', { defaultValue: 'Go to my event' })}
      </Button>
    </Card>
  );
};

export const HomeGridSkeleton: React.FC = () => (
  <Grid gutter="md">
    <Grid.Col span={12}>
      <Skeleton height={200} radius="md" />
    </Grid.Col>
    <Grid.Col span={HALF}>
      <Skeleton height={160} radius="md" />
    </Grid.Col>
    <Grid.Col span={HALF}>
      <Skeleton height={160} radius="md" />
    </Grid.Col>
  </Grid>
);
